import React from 'react'
import { Section, GuideHeader, P, UI, Bullets, Callout, Table } from './GuideParts'

export default function GettingStartedGuide() {
  return (
    <div className="space-y-8">
      <GuideHeader
        title="Getting Started"
        intro="Start here. This page explains what you can do in Xtract AI, which roles exist and where to find things."
      />

      <Section number={1} title="Signing In and Your Role">
        <P>
          You sign in with your Microsoft work account. Your <span className="font-semibold">role</span> is set by an Admin
          and decides which pages you see in the sidebar and which buttons are active. If a page you expect is missing, ask
          your Admin to check your role.
        </P>
        <P>
          Xtract AI reads documents with AI against a <span className="font-semibold">template</span> (the list of fields to
          extract), lets a person review the result, and then sends the approved data on to your other systems.
        </P>
      </Section>

      <Section number={2} title="Roles at a Glance">
        <P>These are the names shown in the app (Users &amp; Groups &gt; Users).</P>
        <Table
          head={['Role', 'What it is for']}
          rows={[
            [<UI key="a">Admin</UI>, 'Manages users, templates and settings, and sees billing, activity and connectors.'],
            [<UI key="t">Template Manager</UI>, 'Creates and edits templates and assigns approvers.'],
            [<UI key="ap">Approver</UI>, 'Reviews, approves and publishes requests.'],
            [<UI key="r">Reviewer</UI>, 'Checks extracted data and sends requests for review. Cannot publish.'],
            [<UI key="u">User</UI>, 'Processes documents with the templates they have been given.'],
            [<UI key="rd">Reader</UI>, 'View-only access to results (only where your organisation has switched this role on).'],
          ]}
        />
        <Callout kind="info" title="Good to know">
          In the app, “Approver” and “Reviewer” are the labels you see. If you ever meet the raw role names in an export or a log,
          Approver is stored as <span className="font-mono">reviewer</span> and Reviewer is stored as <span className="font-mono">sender</span>.
        </Callout>
        <P>
          If your organisation uses OpCos, you may also be a <span className="font-semibold">Super Admin</span> or belong to one OpCo (an Admin is the manager of their OpCo).
          That sits on top of the roles above. See “Working in an OpCo”.
        </P>
      </Section>

      <Section number={3} title="Who Sees Which Page">
        <Table
          head={['Page', 'Admin', 'Template Manager', 'Approver', 'Reviewer', 'User']}
          rows={[
            ['Dashboard, Requests, Email Queue, Documents', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'],
            ['Approvals', 'Yes', 'Yes', 'Yes', 'No', 'Yes'],
            ['Templates', 'Yes', 'Yes', 'No', 'No', 'No'],
            ['Activity, Connectors, Output Destinations, Billing & Analytics, Users & Groups, Settings', 'Yes', 'No', 'No', 'No', 'No'],
            ['Learning, What’s New, Guide', 'Yes', 'Yes', 'Yes', 'Yes', 'Yes'],
          ]}
        />
        <P>A Reader sees the same pages as an Admin, but cannot create, edit or delete anything.</P>
      </Section>

      <Section number={4} title="The Sidebar">
        <Bullets>
          <li><span className="font-semibold">Workspace:</span> Dashboard, Templates, Requests, Approvals, Email Queue, Documents, Activity.</li>
          <li><span className="font-semibold">Administration:</span> Connectors, Output Destinations, Billing &amp; Analytics, Users &amp; Groups, Settings.</li>
          <li><span className="font-semibold">Learning:</span> short how-to videos.</li>
          <li><span className="font-semibold">Help:</span> What’s New and this Guide.</li>
        </Bullets>
        <P>
          Connectors and Output Destinations only appear when your organisation has them switched on.
          The breadcrumb at the top of each page shows where you are.
        </P>
      </Section>

      <Section number={5} title="Notifications">
        <P>
          The bell in the top bar lists what needs your attention, for example a request that is ready for review or one that
          failed. Use <UI>All</UI> or <UI>Unread</UI> to filter. A short pop-up appears when something new arrives.
        </P>
      </Section>

      <Section number={6} title="What’s New, Learning and Support">
        <Bullets>
          <li><UI>What’s New</UI> lists recent changes to the product.</li>
          <li><UI>Learning</UI> has short videos. New videos appear after a page refresh, nothing to install.</li>
          <li>The <UI>Contact &amp; Support</UI> page links to the support address and to this Guide.</li>
        </Bullets>
      </Section>
    </div>
  )
}
